import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import Modal from "react-modal";
import { Edit, X, Save } from "lucide-react";
import { adminServices } from "../services/AdminServices";
import largeNoImage from "../icons/inventory/item-master/large-noimage.png";

// Set app element for react-modal
Modal.setAppElement('#root');

const normalizeUom = (u) => {
  if (!u) return null;
  const id = u.id ?? u.uomId ?? u.uom_id ?? u.UomId ?? u.ID ?? u.Id;
  const name = u.name ?? u.uomName ?? u.uom_name ?? u.UomName ?? u.Name;
  if (id == null || String(name || "").trim() === "") return null;
  return { id, name: String(name).trim() };
};

const resolveUomName = (val) => {
  if (!val && val !== 0) return "";
  if (typeof val === "object") return val.name ?? "";
  return String(val);
};

const extractConversionUoms = (conversions) => {
  if (!Array.isArray(conversions)) return [];
  const map = new Map();
  conversions.forEach((row) => {
    [
      row?.baseUnitNavigation,
      row?.convertedUnitNavigation,
      typeof row?.baseUnit === "object" ? row.baseUnit : null,
      typeof row?.convertedUnit === "object" ? row.convertedUnit : null,
    ].forEach((u) => {
      const normalized = normalizeUom(u);
      if (normalized) map.set(String(normalized.id), normalized);
    });
  });
  return Array.from(map.values());
};

const ProductDetailModal = ({ product, isOpen, onClose, loading, onSave }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedProduct, setEditedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageProduct, setImageProduct] = useState("");
  const [uomConversions, setUomConversions] = useState([]);
  const [uomOptions, setUomOptions] = useState([]);
  const [uomLoading, setUomLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !product) return;

    const productId = product?.id ?? product?.productId ?? product?.originalData?.id;
    if (!productId) {
      setImageProduct("");
      return;
    }

    setImageProduct("");
    adminServices
      .storeProcedure2("getProductImage", { productId: Number(productId) })
      .then((res) => {
        setImageProduct(res?.data?.[0]?.image ?? "");
      })
      .catch((err) => {
        console.error("Failed to load product image", err);
        setImageProduct("");
      });

    const originalConversions =
      product?.originalData?.uomConversions ??
      product?.originalData?.uom_conversions ??
      product?.uomConversions ??
      [];
    if (originalConversions.length > 0) {
      setUomConversions(originalConversions);
      setUomOptions(extractConversionUoms(originalConversions));
    } else {
      setUomLoading(true);
      adminServices
        .getProductUomConversionsByProduct(Number(productId))
        .then((res) => {
          const raw = Array.isArray(res?.data) ? res.data : [];
          setUomConversions(raw);
          setUomOptions(extractConversionUoms(raw));
        })
        .catch((err) => {
          console.error("Failed to load product UOM conversions", err);
          setUomConversions([]);
          setUomOptions([]);
        })
        .finally(() => setUomLoading(false));
    }
  }, [isOpen, product]);

  if (!product && !loading) return null;

  // When loading, render modal with loading state only (avoid accessing product)
  if (loading && !product) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1000 },
          content: {
            position: 'relative',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            margin: 'auto',
            padding: '0',
            outline: 'none',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          }
        }}
        contentLabel="Product Detail"
      >
        <div className="product-detail-modal-content">
          <button className="product-detail-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
          <div style={{ padding: "60px 24px", textAlign: "center", color: "#666" }}>
            Loading product details...
          </div>
        </div>
      </Modal>
    );
  }

  const currentProduct = editedProduct || product;

  const handleEditClick = async () => {
    if (isEditMode) {
      if (onSave && editedProduct) {
        setSaving(true);
        try {
          await onSave(editedProduct);
          setIsEditMode(false);
          setEditedProduct(null);
        } catch {
          // Error handled by parent (toast); stay in edit mode
        } finally {
          setSaving(false);
        }
      } else {
        setIsEditMode(false);
        setEditedProduct(null);
      }
    } else {
      const next = { ...product };
      const currentUomName = String(product?.uom || "").trim();
      if (currentUomName && uomOptions.length > 0) {
        const matched = uomOptions.find(
          (u) => String(u.name).toLowerCase() === currentUomName.toLowerCase()
        );
        if (matched) next.uomId = matched.id;
      }
      setEditedProduct(next);
      setIsEditMode(true);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedProduct({
      ...currentProduct,
      [field]: value
    });
  };

  const handleUomChange = (uomId) => {
    const selected = uomOptions.find((u) => String(u.id) === String(uomId));
    setEditedProduct({
      ...currentProduct,
      uomId: selected ? selected.id : uomId,
      uom: selected ? selected.name : ""
    });
  };

  const handleClose = () => {
    setIsEditMode(false);
    setEditedProduct(null);
    onClose();
  };

  // Fill price history to minimum 5 rows
  const priceHistoryData = [...(currentProduct.priceHistory || [])];
  const minRows = 5;
  while (priceHistoryData.length < minRows) {
    priceHistoryData.push({
      id: priceHistoryData.length + 1,
      timestamp: "",
      priceBefore: "",
      priceAfter: "",
      editedBy: {
        name: "",
        email: "",
        avatar: ""
      }
    });
  }

  const priceHistoryColumns = [
    {
      name: "Timestamps",
      selector: row => row.timestamp || "-",
      sortable: true,
      width: "25%"
    },
    {
      name: "Price Before",
      selector: row => row.priceBefore || "-",
      sortable: true,
      width: "20%"
    },
    {
      name: "Price After",
      selector: row => row.priceAfter || "-",
      sortable: true,
      width: "20%"
    },
    {
      name: "Edited by",
      cell: row => {
        const editedBy = row.editedBy;
        if (!editedBy || (!editedBy.name && !editedBy.email)) {
          return "-";
        }
        return (
          <div className="product-editor-info">
            {editedBy.avatar && (
              <img 
                src={editedBy.avatar} 
                alt={editedBy.name}
                className="product-editor-avatar"
              />
            )}
            <div>
              {editedBy.name && <div className="product-editor-name">{editedBy.name}</div>}
              {editedBy.email && <div className="product-editor-email">{editedBy.email}</div>}
            </div>
          </div>
        );
      },
      sortable: false,
      width: "35%"
    }
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000
        },
        content: {
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          margin: 'auto',
          padding: '0',
          outline: 'none',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      contentLabel="Product Detail"
    >
      <div className="product-detail-modal-content">
        <button 
          className="product-detail-modal-close"
          onClick={handleClose}
        >
          <X size={24} />
        </button>

        {!product ? null : (
        <>
        <div className="product-detail-header">
          <div className="product-detail-image">
            <img 
              src={imageProduct || currentProduct.image || largeNoImage}
              alt={currentProduct.itemName}
            />
          </div>
          <div className="product-detail-info">
            <div className="product-detail-title-row">
              <h2 className="product-detail-name">{currentProduct.itemName}</h2>
              <button 
                className="product-detail-edit-btn"
                onClick={handleEditClick}
                disabled={saving}
              >
                {saving ? "Saving..." : isEditMode ? <Save size={20} /> : <Edit size={20} />}
              </button>
            </div>
            <p className="product-detail-sku">{currentProduct.sku}</p>
          </div>
        </div>

        <div className="product-detail-attributes">
          <div className="product-attribute-field">
            <label>Unit Price</label>
            {isEditMode ? (
              <input
                type="text"
                className="product-attribute-input"
                value={currentProduct.unitPrice}
                onChange={(e) => handleFieldChange('unitPrice', e.target.value)}
              />
            ) : (
              <div className="product-attribute-value">{currentProduct.unitPrice}</div>
            )}
          </div>
          <div className="product-attribute-field">
            <label>UOM</label>
            {isEditMode ? (
              <select
                className="product-attribute-input"
                value={currentProduct.uomId ?? ""}
                onChange={(e) => handleUomChange(e.target.value)}
                disabled={uomLoading}
              >
                <option value="">{uomLoading ? "Loading..." : "Select UOM"}</option>
                {uomOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="product-attribute-value">{currentProduct.uom}</div>
            )}
          </div>
          <div className="product-attribute-field">
            <label>MOQ (Min. order quantity)</label>
            {isEditMode ? (
              <input
                type="number"
                className="product-attribute-input"
                value={currentProduct.moq}
                onChange={(e) => handleFieldChange('moq', e.target.value)}
              />
            ) : (
              <div className="product-attribute-value">{currentProduct.moq}</div>
            )}
          </div>
          <div className="product-attribute-field">
            <label>Lead Time</label>
            {isEditMode ? (
              <input
                type="text"
                className="product-attribute-input"
                value={currentProduct.leadTime}
                onChange={(e) => handleFieldChange('leadTime', e.target.value)}
              />
            ) : (
              <div className="product-attribute-value">{currentProduct.leadTime}</div>
            )}
          </div>
        </div>

        <div className="product-price-history-section">
          <h3 className="product-price-history-title">Price History</h3>
          <div className="product-price-history-table-container">
            <div className="product-price-history-table-wrapper">
              <DataTable
                columns={priceHistoryColumns}
                data={priceHistoryData}
                customStyles={customStyles}
                striped
                highlightOnHover
                noHeader={false}
                pagination={false}
              />
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </Modal>
  );
};

export default ProductDetailModal;

